import {
  busLabel, bbLocal, bbWorld, frameItems, labelItems, LIB, netNumberItems, pinNode, pinsW, potentialColour, potentialOf, symbolSvg, standardOf, svgFor, type DrawingStandard,
  SHEET, terminalSet, underlayItems, wireNode, type Analysis, type Colour, type Item, type Project, type Sym,
} from '@enercad/core';
import { esc } from '../ui';

/* Screen rendering: turns core display-list items into SVG. Units are mm. */

const cvar = (c?: Colour) => (c ? `var(--${c})` : 'none');
const FONT = { ui: 'var(--ui)', mono: 'var(--mono)', cond: 'var(--cond)' } as const;
const r2 = (v: number) => Math.round(v * 100) / 100;

export function itemsToSvg(items: Item[]): string {
  let o = '';
  for (const it of items) {
    if (it.k === 'poly') {
      const pts = it.pts.map((p) => `${r2(p[0])},${r2(p[1])}`).join(' ');
      o += `<${it.closed ? 'polygon' : 'polyline'} class="ly-${it.layer}" points="${pts}" fill="${cvar(it.fill)}" stroke="${it.stroke ? cvar(it.stroke) : 'none'}" stroke-width="${it.width}"${it.dash ? ` stroke-dasharray="${it.dash.join(' ')}"` : ''} stroke-linecap="round" stroke-linejoin="round"/>`;
    } else if (it.k === 'circle') {
      o += `<circle class="ly-${it.layer}" cx="${r2(it.c[0])}" cy="${r2(it.c[1])}" r="${it.r}" fill="${cvar(it.fill)}" stroke="${it.stroke ? cvar(it.stroke) : 'none'}" stroke-width="${it.width}"/>`;
    } else if (it.k === 'image') {
      // front-view picture of a part, served by the API (same origin, session cookie)
      const href = it.src.startsWith('data:') ? it.src : `/api/parts/${encodeURIComponent(it.src)}/front.img`;
      o += `<image class="ly-${it.layer}" href="${href}" x="${r2(it.x)}" y="${r2(it.y)}" width="${r2(it.w)}" height="${r2(it.h)}" preserveAspectRatio="${it.src.startsWith('data:') ? 'xMinYMid meet' : 'none'}"/>`;
    } else {
      const rot = it.rot ? ` transform="rotate(${it.rot} ${r2(it.x)} ${r2(it.y)})"` : '';
      o += `<text class="ly-${it.layer}" x="${r2(it.x)}" y="${r2(it.y)}" font-size="${it.size}" text-anchor="${it.anchor}" fill="${cvar(it.colour)}" font-family="${FONT[it.font]}"${it.bold ? ' font-weight="600"' : ''}${rot}>${esc(it.text)}</text>`;
    }
  }
  return o;
}

export function paperSVG(pr: Project, pi: number, grid: boolean): string {
  const { W, H, X0, X1, Y0, HDR, TB_Y } = SHEET;
  let o = `<rect class="paper" x="0" y="0" width="${W}" height="${H}"/>`;
  if (grid) o += `<rect x="${X0}" y="${Y0 + HDR}" width="${X1 - X0}" height="${TB_Y - Y0 - HDR}" fill="url(#gd)"/>`;
  // imported drawing to trace over, behind the frame and everything drawn
  const u = pr.pages[pi].underlay;
  if (u) o += `<g class="underlay">${itemsToSvg(underlayItems(u))}</g>`;
  return o + itemsToSvg(frameItems(pr, { no: String(pi + 1), desc: pr.pages[pi].desc, total: String(pr.pages.length), pi }));
}

export interface PageLayers {
  wires: string;
  syms: string;
  labels: string;
  selection: string;
}

export function pageLayers(pr: Project, A: Analysis, pi: number, sel: Set<string>, showNetNo: boolean): PageLayers {
  const std = standardOf(pr);
  const page = pr.pages[pi];
  let wires = '', hits = '', syms = '', labels = '', selection = '';
  const netLabels: Item[] = [];
  for (const w of page.wires) {
    const on = sel.has('w:' + w.id), pts = w.pts.map((p) => p[0] + ',' + p[1]).join(' ');
    wires += `<polyline class="wire${on ? ' on' : ''}${w.bus ? ' bus' : ''}" points="${pts}" style="stroke:${cvar(potentialColour(potentialOf(A, wireNode(w.id))))}"/>`;
    const bl = busLabel(w, page);
    if (bl) netLabels.push(bl);
    hits += `<polyline class="whit" data-wid="${w.id}" points="${pts}"/>`;
    if (showNetNo) netLabels.push(...netNumberItems(A, w.id, w.pts));
  }
  for (const j of A.junctions[pi] || [])
    wires += `<circle class="jn" cx="${j.x}" cy="${j.y}" r=".75" style="fill:${cvar(potentialColour(potentialOf(A, j.node)))}"/>`;
  const ts = terminalSet(page.syms);
  for (const s of page.syms) {
    const L = LIB[s.type], on = sel.has('s:' + s.id);
    if (L.role === 'text') {
      labels += `<text class="tx${on ? ' on' : ''}" data-sid="${s.id}" x="${s.x}" y="${s.y}" font-size="${s.props.size || 3.5}">${esc(s.props.text || '')}</text>`;
    } else {
      const b = bbLocal(s);
      syms += `<g class="sym${on ? ' on' : ''}" data-sid="${s.id}" transform="translate(${s.x} ${s.y}) rotate(${s.rot * 90})"><rect class="hit" x="${b[0] - 1}" y="${b[1] - 1}" width="${b[2] - b[0] + 2}" height="${b[3] - b[1] + 2}"/>${symbolSvg(s, std)}</g>`;
      labels += itemsToSvg(labelItems(A, s, ts));
    }
    if (on) {
      const B = bbWorld(s);
      selection += `<rect class="selbox" x="${B[0] - 1.2}" y="${B[1] - 1.2}" width="${B[2] - B[0] + 2.4}" height="${B[3] - B[1] + 2.4}"/>`;
    }
  }
  return { wires: wires + hits, syms, labels: `<g class="lbls">${labels}${itemsToSvg(netLabels)}</g>`, selection };
}

/** Unconnected pin markers shown while drawing connections. */
export function openPinMarkers(A: Analysis, syms: Sym[]): string {
  let o = '';
  for (const s of syms)
    pinsW(s).forEach((p, i) => {
      if ((A.size.get(A.uf.find(pinNode(s, i))) || 1) < 2) o += `<circle class="pinmk" cx="${p[0]}" cy="${p[1]}" r=".7"/>`;
    });
  return o;
}

export function symbolPreview(key: string, w = 46, h = 34, std: DrawingStandard = 'iec'): string {
  const L = LIB[key];
  if (L.role === 'text')
    return `<svg viewBox="0 0 20 14" width="${w}" height="${h}"><text x="10" y="9.5" font-size="7" text-anchor="middle" fill="var(--ink)" font-family="var(--ui)">Abc</text></svg>`;
  const b = L.bb, pad = 3, vw = b[2] - b[0] + pad * 2, vh = b[3] - b[1] + pad * 2, m = Math.max(vw, vh * 1.3);
  return `<svg viewBox="${b[0] - pad - (m - vw) / 2} ${b[1] - pad - (m / 1.3 - vh) / 2} ${m} ${m / 1.3}" width="${w}" height="${h}" aria-hidden="true"><g class="sym">${svgFor(key, std)}</g></svg>`;
}
